import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from './useDebounce';

export const PAGE_SIZE = 20;

// Select options loaded PAGE_SIZE at a time: the next page when the dropdown is
// scrolled to the bottom, back to page 1 on a new search or when `reloadKey`
// changes. `fetchPage(page, limit, search)` resolves to `{ items, totalPages }`,
// or null on failure. Pass the returned handlers to an antd Select along with
// `filterOption={false}` (FloatingLabelSearchSelect already sets it).
export const useInfiniteOptions = (fetchPage, { enabled = true, reloadKey } = {}) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  // Only the latest request may write state; a page that lands after a new
  // search or reload is dropped.
  const requestId = useRef(0);
  const loadingRef = useRef(false);

  const load = useCallback(async (nextPage, term) => {
    const id = ++requestId.current;
    loadingRef.current = true;
    setLoading(true);
    const result = await fetchRef.current(nextPage, PAGE_SIZE, term);
    if (id !== requestId.current) return;
    loadingRef.current = false;
    setLoading(false);
    if (!result) return;
    setItems((prev) => (nextPage === 1 ? result.items : [...prev, ...result.items]));
    setPage(nextPage);
    setTotalPages(result.totalPages || 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load(1, debouncedSearch);
  }, [enabled, reloadKey, debouncedSearch, load]);

  const onPopupScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollTop + clientHeight < scrollHeight - 20) return;
    if (loadingRef.current || page >= totalPages) return;
    load(page + 1, debouncedSearch);
  };

  return { items, loading, onSearch: setSearch, onPopupScroll };
};

// `options` with `selected` prepended when it isn't among them, so a Select
// keeps showing the chosen label after a search or reload drops it. `key` is
// the id field (`_id` for raw records).
export const withSelectedOption = (options, selected, key = 'value') =>
  selected && !options.some((o) => o[key] === selected[key]) ? [selected, ...options] : options;
